import { Fragment } from 'react';
import _ from 'lodash';
import { Chip, Tooltip } from '@material-ui/core';
import Icon from 'components/base/Icon';
import { useSelector } from 'react-redux';
import { teamsProjectsByTeamIdSelector } from 'state/selectors/projects';
import { LabelStyled, TeamIcon } from './LabelsList.style';
import {
  chooseLabel,
  chooseTooltip,
  shouldShowToolTip
} from './LabelsList.service';
import Text, { Inline } from './base/Text';
import { Flex } from './base/layout-components';
import TooltipWrapper from './Tooltips/labels-list/TooltipWrapper';
import useOrgTeams from './FilterMenus/useOrgTeams';
import { LBButton, LBXIcon } from '@linearb/buzz';

export const TeamIconWrapper = ({ color }) => {
  return (
    <Inline pr='0.5rem'>
      <TeamIcon teamColor={color} items='center'>
        <Icon name='user' size='2rem' color='white' />
      </TeamIcon>
    </Inline>
  );
};

export const TeamLabel = ({ title, color, isEmptyFilters }) => {
  return (
    <>
      <TeamIconWrapper color={color} data-testid={`team-icon-${title}`} />
      <p data-testid={`team-label-title-${title}`}>{title}</p>
      {isEmptyFilters && (
        <Icon
          name='Alert'
          color='radicalRed'
          size='smaller'
          style={{ marginLeft: '0.5rem' }}
        />
      )}
    </>
  );
};

export const LabelItem = ({
  labelItem,
  onDelete,
  projectsByTeamId,
  teamsByIds,
  allStatuses,
  titleKey = 'title',
  withOperators = true
}) => {
  const { key, label, values } = labelItem;
  return (
    <>
      {label && (
        <span data-cy='label-key' data-testid='label-key'>
          {label}
        </span>
      )}

      {values?.map(({ id, [titleKey]: title, color, entity }, index) => {
        return (
          <>
            {title && (
              <>
                <Tooltip
                  key={index}
                  placement='bottom'
                  interactive
                  disableHoverListener={
                    !shouldShowToolTip({ key, allStatuses, title })
                  }
                  title={
                    <TooltipWrapper
                      tooltip={chooseTooltip({
                        key,
                        id,
                        entity,
                        allStatuses,
                        title,
                        projectsByTeamId,
                        teamsByIds
                      })}
                    />
                  }
                  data-testid='label-tooltip'
                >
                  <Chip
                    key={index}
                    label={chooseLabel({
                      id,
                      key,
                      title,
                      color,
                      projectsByTeamId,
                      teamsByIds
                    })}
                    deleteIcon={
                      <LBButton variant='link' size='icon'>
                        <LBXIcon
                          name='Close'
                          data-testid={`delete-icon-${key}-${id}`}
                        />
                      </LBButton>
                    }
                    {...(onDelete ? { onDelete: () => onDelete(key, id) } : {})}
                  />
                </Tooltip>
                {index < values.length - 1 && withOperators && (
                  <OperatorContainer operator='OR' />
                )}
              </>
            )}
          </>
        );
      })}
    </>
  );
};

export const OperatorContainer = ({ operator = 'AND' }) => {
  return (
    <Flex mr='small' ml='x-small' items='center'>
      <Text weight='bold' size='normal'>
        {operator}
      </Text>
    </Flex>
  );
};

const LabelsList = ({
  list,
  onDelete,
  allStatuses = undefined,
  withOperators = true
}) => {
  const orgTeams = useOrgTeams();
  const projectsByTeamId = useSelector(teamsProjectsByTeamIdSelector);
  if (_.isEmpty(list)) return null;
  const teamsByIds = _.keyBy(orgTeams, 'id');

  // flat all custom fields to be in same level as other entities
  const flatteredList = list.reduce((acc, item) => {
    if (item.isGroup && item.groupItems) {
      acc.push(...item.groupItems);
    } else {
      acc.push(item);
    }
    return acc;
  }, []);
  return flatteredList.map((item, idx) => {
    const { isGroup, groupItems, values } = item;
    const isEmptyGroup = isGroup && _.isEmpty(groupItems);
    const isEmptyValues = !isGroup && _.isEmpty(values);
    if (isEmptyGroup || isEmptyValues) return null;
    const hasNextItemWithValues = flatteredList
      .slice(idx + 1)
      .some(
        nextItem =>
          !_.isEmpty(nextItem.values) || !_.isEmpty(nextItem.groupItems)
      );
    return (
      <Fragment key={idx}>
        <LabelStyled items='center' key={idx} data-cy='label'>
          {isGroup ? (
            groupItems.map((item, idx) => (
              <>
                <LabelItem
                  labelItem={item}
                  onDelete={onDelete}
                  key={item.key}
                  withOperators={withOperators}
                />
                {idx < groupItems.length - 1 && (
                  <OperatorContainer operator='OR' />
                )}
              </>
            ))
          ) : (
            <LabelItem
              labelItem={item}
              onDelete={onDelete}
              projectsByTeamId={projectsByTeamId}
              teamsByIds={teamsByIds}
              allStatuses={allStatuses}
              withOperators={withOperators}
            />
          )}
        </LabelStyled>
        {hasNextItemWithValues && withOperators && <OperatorContainer />}
      </Fragment>
    );
  });
};

export default LabelsList;
